// Icon.tsx - simple line glyphs (stroke = currentColor).
// Ported from _maquette/app/tokens.jsx.
import type { CSSProperties, ReactElement } from "react";

/** Every glyph key supported by <Icon />. */
export type IconName =
  | "phone"
  | "route"
  | "pin"
  | "list"
  | "map"
  | "bell"
  | "gear"
  | "search"
  | "clock"
  | "users"
  | "plus"
  | "minus"
  | "x"
  | "check"
  | "chevR"
  | "chevL"
  | "chevD"
  | "arrowUp"
  | "star"
  | "plate"
  | "location"
  | "lock"
  | "flame"
  | "edit"
  | "logout"
  | "home"
  | "photo"
  | "mail"
  | "refresh"
  | "calendar"
  | "thumb";

export interface IconProps {
  name: IconName;
  size?: number;
  stroke?: number;
  color?: string;
  fill?: string;
  style?: CSSProperties;
}

export function Icon({
  name,
  size = 22,
  stroke = 2,
  color,
  fill = "none",
  style,
}: IconProps): ReactElement {
  const p = {
    fill: "none",
    stroke: color || "currentColor",
    strokeWidth: stroke,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  const paths: Record<IconName, ReactElement> = {
    phone: (
      <path
        {...p}
        d="M5 4h3l1.5 4-2 1.5a11 11 0 005 5l1.5-2 4 1.5v3a2 2 0 01-2 2A15 15 0 013 6a2 2 0 012-2z"
        fill={fill}
      />
    ),
    route: (
      <g {...p}>
        <circle cx="6" cy="18" r="2.4" />
        <circle cx="18" cy="6" r="2.4" />
        <path d="M8 16.5c5-1 7-3 8.2-8" />
      </g>
    ),
    pin: (
      <g {...p}>
        <path d="M12 21s7-6.2 7-11a7 7 0 10-14 0c0 4.8 7 11 7 11z" fill={fill} />
        <circle
          cx="12"
          cy="10"
          r="2.5"
          fill={fill === "none" ? "none" : "#fff"}
          stroke={fill === "none" ? color || "currentColor" : "#fff"}
        />
      </g>
    ),
    list: (
      <g {...p}>
        <path d="M8 7h12M8 12h12M8 17h12" />
        <circle cx="4" cy="7" r="0.6" fill={color || "currentColor"} />
        <circle cx="4" cy="12" r="0.6" fill={color || "currentColor"} />
        <circle cx="4" cy="17" r="0.6" fill={color || "currentColor"} />
      </g>
    ),
    map: (
      <g {...p}>
        <path d="M9 4L3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5 9 4z" />
        <path d="M9 4v13M15 6.5v13" />
      </g>
    ),
    bell: (
      <path
        {...p}
        d="M6 9a6 6 0 0112 0c0 5 2 6 2 6H4s2-1 2-6zM10 20a2 2 0 004 0"
        fill={fill}
      />
    ),
    gear: (
      <g {...p}>
        <circle cx="12" cy="12" r="3.2" />
        <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
      </g>
    ),
    search: (
      <g {...p}>
        <circle cx="11" cy="11" r="6.5" />
        <path d="M16 16l4 4" />
      </g>
    ),
    clock: (
      <g {...p}>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 7.5V12l3 2" />
      </g>
    ),
    users: (
      <g {...p}>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 19c0-3.3 2.7-5 6-5s6 1.7 6 5" />
        <path d="M16 6.5a2.8 2.8 0 010 5.4M21 19c0-2.6-1.4-4.2-3.5-4.8" />
      </g>
    ),
    plus: <path {...p} d="M12 5v14M5 12h14" />,
    minus: <path {...p} d="M5 12h14" />,
    x: <path {...p} d="M6 6l12 12M18 6L6 18" />,
    check: <path {...p} d="M5 12.5l5 5 9-11" />,
    // Pouce levé - action « Confirmer » du volet de swipe (docs/rules/confirmation-resa.md).
    thumb: <path {...p} d="M7 11v9H4a1 1 0 01-1-1v-7a1 1 0 011-1h3zm0 0l4-8a2 2 0 012 2v4h5a2 2 0 012 2.3l-1 6.5a2 2 0 01-2 1.7H7" fill={fill} />,
    chevR: <path {...p} d="M9 5l7 7-7 7" />,
    chevL: <path {...p} d="M15 5l-7 7 7 7" />,
    chevD: <path {...p} d="M5 9l7 7 7-7" />,
    arrowUp: <path {...p} d="M12 19V5M6 11l6-6 6 6" />,
    star: (
      <path
        {...p}
        d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 17l-5.2 2.6 1-5.8-4.3-4.1 5.9-.9L12 3.5z"
        fill={fill}
      />
    ),
    plate: (
      <g {...p}>
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="12" r="4.2" />
      </g>
    ),
    location: (
      <g {...p}>
        <circle cx="12" cy="12" r="3" fill={fill} />
        <circle cx="12" cy="12" r="8" />
        <path d="M12 1v3M12 20v3M1 12h3M20 12h3" />
      </g>
    ),
    lock: (
      <g {...p}>
        <rect x="5" y="10" width="14" height="10" rx="2.2" fill={fill} />
        <path d="M8 10V7.5a4 4 0 018 0V10" />
        <circle cx="12" cy="15" r="1.3" fill={color || "currentColor"} />
      </g>
    ),
    flame: (
      <path
        {...p}
        d="M12 3c1 3-1.5 4-1.5 6.5A2.5 2.5 0 0013 12c.5-1 .3-2 .3-2 1.5 1.2 2.7 3 2.7 5a4 4 0 01-8 0c0-3 2-5 4-12z"
        fill={fill}
      />
    ),
    edit: (
      <g {...p}>
        <path d="M4 20h4L19 9l-4-4L4 16v4z" />
        <path d="M14 6l4 4" />
      </g>
    ),
    logout: (
      <g {...p}>
        <path d="M14 4h4a1 1 0 011 1v14a1 1 0 01-1 1h-4" />
        <path d="M3 12h11M10 8l4 4-4 4" />
      </g>
    ),
    home: <path {...p} d="M4 11l8-7 8 7M6 9.5V20h12V9.5" fill={fill} />,
    photo: (
      <g {...p}>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <circle cx="8.5" cy="10" r="1.6" />
        <path d="M21 16l-5-4-9 7" />
      </g>
    ),
    mail: (
      <g {...p}>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M3.5 6.5L12 12.5l8.5-6" />
      </g>
    ),
    refresh: (
      <g {...p}>
        <path d="M3.5 12a8.5 8.5 0 0114.5-6M20.5 12a8.5 8.5 0 01-14.5 6" />
        <path d="M18 3v3.5h-3.5M6 21v-3.5h3.5" />
      </g>
    ),
    calendar: (
      <g {...p}>
        <rect x="3.5" y="5" width="17" height="15.5" rx="2.2" fill={fill} />
        <path d="M3.5 9.5h17M8 2.8V6M16 2.8V6" />
        <circle cx="8.2" cy="13.5" r="0.7" fill={color || "currentColor"} />
        <circle cx="12" cy="13.5" r="0.7" fill={color || "currentColor"} />
        <circle cx="15.8" cy="13.5" r="0.7" fill={color || "currentColor"} />
      </g>
    ),
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      style={{ display: "block", flexShrink: 0, ...style }}
    >
      {paths[name]}
    </svg>
  );
}
